import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useUser } from '../../context/UserContext';
import metaverseService from '../../services/metaverseService';
import neuroAdaptiveService from '../../services/neuroAdaptiveService';
import './MetaverseWorld.css';

function MetaverseWorld() {
  const { user } = useUser();
  const [isLoading, setIsLoading] = useState(true);
  const [currentZone, setCurrentZone] = useState(null);
  const [zones, setZones] = useState([]);
  const [player, setPlayer] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [showZoneMap, setShowZoneMap] = useState(false);
  const [showAvatarCustomizer, setShowAvatarCustomizer] = useState(false);
  const [neuroActive, setNeuroActive] = useState(false);
  const [adaptations, setAdaptations] = useState(null);
  const canvasRef = useRef(null);

  const addSystemMessage = useCallback((message) => {
    setChatMessages(prev => [...prev, {
      id: Date.now(),
      type: 'system',
      message,
      timestamp: Date.now()
    }]);
  }, []);

  const showNotification = useCallback((message, type) => {
    // Simple notification - could be enhanced with a toast library
    const notification = document.createElement('div');
    notification.className = `notification ${type}`;
    notification.textContent = message;
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.remove();
    }, 3000);
  }, []);

  const handleZoneEntered = useCallback((e) => {
    const { zone, playersInZone } = e.detail;
    setCurrentZone(zone);
    
    // Show zone welcome message
    addSystemMessage(`Welcome to ${zone.name}! ${playersInZone} players here.`);
  }, [addSystemMessage]);

  const handleMetaverseUpdate = useCallback((e) => {
    const { event, data } = e.detail;
    
    switch (event) {
      case 'chat_message':
        setChatMessages(prev => [...prev, data]);
        break;
      case 'player_joined':
        addSystemMessage(`${data.playerName} joined the metaverse`);
        break;
      case 'player_left':
        addSystemMessage(`${data.playerName} left the metaverse`);
        break;
      default:
        // Unknown event type
        break;
    }
  }, [addSystemMessage]);

  const handleNeuroAdaptation = useCallback((e) => {
    const adaptation = e.detail;
    setAdaptations(adaptation);
    
    // Show adaptation notification
    if (adaptation.action === 'show_hint') {
      showNotification('💡 Need help? Hint available!', 'info');
    } else if (adaptation.action === 'increase_difficulty') {
      showNotification('🚀 You\'re doing great! Try a harder challenge?', 'success');
    } else if (adaptation.action === 'show_example') {
      showNotification('📚 Let me show you an example!', 'info');
    }
  }, [showNotification]);

  const initializeMetaverse = useCallback(async () => {
    if (!user) return;
    
    try {
      // Initialize metaverse
      const result = await metaverseService.initialize(
        'metaverse-canvas',
        user.uid,
        user.name || 'Student'
      );
      
      if (result.success) {
        setPlayer(result.player);
        setZones(metaverseService.getZones());
        setCurrentZone(metaverseService.getCurrentZone());
        
        // Start neuro-adaptive learning
        await neuroAdaptiveService.initialize(user.uid, {
          trackTyping: true,
          trackFocus: true,
          trackMouse: true,
          privacyMode: true
        });
        
        setNeuroActive(true);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error initializing:', error);
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    initializeMetaverse();
    
    // Listen for metaverse events
    document.addEventListener('zone-entered', handleZoneEntered);
    document.addEventListener('metaverse-update', handleMetaverseUpdate);
    document.addEventListener('neuro-adaptation', handleNeuroAdaptation);
    
    return () => {
      document.removeEventListener('zone-entered', handleZoneEntered);
      document.removeEventListener('metaverse-update', handleMetaverseUpdate);
      document.removeEventListener('neuro-adaptation', handleNeuroAdaptation);
      metaverseService.disconnect();
      neuroAdaptiveService.stop();
    };
  }, [user, initializeMetaverse, handleZoneEntered, handleMetaverseUpdate, handleNeuroAdaptation]);

  const handleMove = (direction) => {
    metaverseService.movePlayer(direction, 2);
    setPlayer({ ...metaverseService.currentPlayer });
    setCurrentZone(metaverseService.getCurrentZone());
  };

  const handleSendChat = () => {
    if (!chatInput.trim()) return;
    
    metaverseService.sendChatMessage(chatInput, 'zone');
    setChatInput('');
  };

  const handleTeleport = (zoneId) => {
    const result = metaverseService.teleportToZone(zoneId);
    if (result.success) {
      setCurrentZone(result.zone);
      setShowZoneMap(false);
    }
  };

  const handleEmote = (emote) => {
    metaverseService.performEmote(emote);
  };

  if (isLoading) {
    return (
      <div className="metaverse-loading">
        <div className="loading-spinner"></div>
        <h2>🌍 Loading Metaverse...</h2>
        <p>Preparing your virtual world</p>
      </div>
    );
  }

  return (
    <div className="metaverse-world">
      {/* Header */}
      <div className="metaverse-header">
        <div className="header-left">
          <h1>🌍 Coding Metaverse</h1>
          {currentZone && (
            <div className="current-zone-info">
              <span className="zone-name">{currentZone.name}</span>
              <span className="zone-players">
                👥 {currentZone.players.length}/{currentZone.capacity}
              </span>
            </div>
          )}
        </div>
        
        <div className="header-right">
          {neuroActive && (
            <div className="neuro-indicator">
              <span className="neuro-icon">🧠</span>
              <span className="neuro-label">AI Learning Active</span>
            </div>
          )}
          
          <button 
            onClick={() => setShowZoneMap(!showZoneMap)}
            className="map-btn"
          >
            🗺️ Map
          </button>
          
          <button 
            onClick={() => setShowAvatarCustomizer(!showAvatarCustomizer)}
            className="avatar-btn"
          >
            👤 Avatar
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="metaverse-content">
        {/* 3D Canvas (Placeholder) */}
        <div className="metaverse-canvas" ref={canvasRef}>
          <div className="canvas-placeholder">
            <div className="zone-visual">
              <h2>{currentZone?.name || 'Welcome Plaza'}</h2>
              <p>{currentZone?.description || 'Your coding adventure begins here!'}</p>
              
              {currentZone?.type === 'learning' && (
                <div className="zone-topic-badge">
                  📚 Topic: {currentZone.topic}
                </div>
              )}
            </div>
            
            {/* Player avatar representation */}
            <div className="player-avatar">
              <div className="avatar-emoji">{player?.avatar.emoji}</div>
              <div className="player-name">{player?.name}</div>
            </div>
          </div>
          
          {/* Movement Controls */}
          <div className="movement-controls">
            <button onClick={() => handleMove('forward')} className="move-btn">
              ⬆️
            </button>
            <div className="horizontal-controls">
              <button onClick={() => handleMove('left')} className="move-btn">
                ⬅️
              </button>
              <button onClick={() => handleMove('backward')} className="move-btn">
                ⬇️
              </button>
              <button onClick={() => handleMove('right')} className="move-btn">
                ⬆️
              </button>
            </div>
          </div>
          
          {/* Emote Bar */}
          <div className="emote-bar">
            <button onClick={() => handleEmote('wave')} className="emote-btn">
              👋
            </button>
            <button onClick={() => handleEmote('dance')} className="emote-btn">
              💃
            </button>
            <button onClick={() => handleEmote('celebrate')} className="emote-btn">
              🎉
            </button>
            <button onClick={() => handleEmote('think')} className="emote-btn">
              🤔
            </button>
          </div>
        </div>

        {/* Chat Sidebar */}
        <div className="chat-sidebar">
          <div className="chat-header">
            <h3>💬 Chat</h3>
            <span className="chat-zone">{currentZone?.name}</span>
          </div>
          
          <div className="chat-messages">
            {chatMessages.map((msg, index) => (
              <div key={index} className={`chat-message ${msg.type || 'player'}`}>
                {msg.type === 'system' ? (
                  <span className="system-message">ℹ️ {msg.message}</span>
                ) : (
                  <>
                    <span className="message-author">{msg.playerName}:</span>
                    <span className="message-text">{msg.message}</span>
                  </>
                )}
              </div>
            ))}
          </div>
          
          <div className="chat-input">
            <input
              type="text"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendChat()}
              placeholder="Say something..."
            />
            <button onClick={handleSendChat}>Send</button>
          </div>
        </div>
      </div>

      {/* Zone Map Modal */}
      {showZoneMap && (
        <div className="modal-overlay" onClick={() => setShowZoneMap(false)}>
          <div className="zone-map-modal" onClick={(e) => e.stopPropagation()}>
            <h2>🗺️ Metaverse Map</h2>
            
            <div className="zones-grid">
              {zones.map(zone => (
                <div 
                  key={zone.id}
                  className={`zone-card ${zone.id === currentZone?.id ? 'current' : ''}`}
                  onClick={() => handleTeleport(zone.id)}
                >
                  <h3>{zone.name}</h3>
                  <p>{zone.description}</p>
                  
                  <div className="zone-stats">
                    <span>👥 {zone.playersInZone}/{zone.capacity}</span>
                    <span className={`zone-type ${zone.type}`}>
                      {zone.type}
                    </span>
                  </div>
                  
                  {zone.id === currentZone?.id && (
                    <span className="current-badge">You are here</span>
                  )}
                  
                  {zone.isFull && (
                    <span className="full-badge">Full</span>
                  )}
                </div>
              ))}
            </div>
            
            <button onClick={() => setShowZoneMap(false)} className="close-btn">
              Close
            </button>
          </div>
        </div>
      )}

      {/* Avatar Customizer Modal */}
      {showAvatarCustomizer && (
        <div className="modal-overlay" onClick={() => setShowAvatarCustomizer(false)}>
          <div className="avatar-customizer-modal" onClick={(e) => e.stopPropagation()}>
            <h2>👤 Customize Avatar</h2>
            
            <div className="avatar-preview">
              <div className="preview-avatar">
                {player?.avatar.emoji}
              </div>
            </div>
            
            <div className="customization-options">
              <div className="option-group">
                <h3>Emoji</h3>
                <div className="emoji-grid">
                  {['😊', '😎', '🤓', '😃', '🥳', '🤩', '😺', '🐶', '🦄', '🤖'].map(emoji => (
                    <button
                      key={emoji}
                      onClick={() => {
                        metaverseService.updateAvatar({ emoji });
                        setPlayer({ ...metaverseService.currentPlayer });
                      }}
                      className={`emoji-option ${player?.avatar.emoji === emoji ? 'selected' : ''}`}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            <button onClick={() => setShowAvatarCustomizer(false)} className="close-btn">
              Done
            </button>
          </div>
        </div>
      )}

      {/* Neuro-Adaptive Overlay */}
      {adaptations && (
        <div className="neuro-adaptation-overlay">
          <div className="adaptation-card">
            <h3>🧠 AI Suggestion</h3>
            <p>{adaptations.reason}</p>
            
            {adaptations.action === 'show_hint' && (
              <button className="adaptation-btn">Show Hint</button>
            )}
            
            {adaptations.action === 'show_example' && (
              <button className="adaptation-btn">View Example</button>
            )}
            
            {adaptations.action === 'increase_difficulty' && (
              <button className="adaptation-btn">Try Harder Challenge</button>
            )}
            
            <button 
              className="dismiss-btn"
              onClick={() => setAdaptations(null)}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default MetaverseWorld;
